import React from 'react'
import { Link } from 'react-router'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { Combobox } from '@klear/ui/components/combobox'
import { cn } from '@klear/ui/lib/utils'
import type { UserSchema, UserFormErrors } from '@klear/ui/lib/user-form-schema'
import { isKlearRoleName, isKlearRoleWire } from '@klear/ui/lib/user-wire'
import {
  ROLE_KLEAR_SUPER_ADMIN,
  ROLE_KLEAR_CSM,
  ROLE_KLEAR_READONLY,
  ROLE_KLEAR_FUNDER,
  ROLE_TRADER_ADMIN,
  ROLE_TRADER_FINANCIAL_ADMIN,
  ROLE_TRADER_ORGANIZATION_OWNER,
  ROLE_TRADER_USER,
  ROLE_TRADER_READONLY,
  ROLE_LABELS
} from '@klear/constants'
import type { UserRoleName } from '@klear/user/user-role'
import type { Trader } from '@klear/trader/trader'
import toNumber from 'lodash/toNumber'
import isNumber from 'lodash/isNumber'

interface UserFormCommonProps {
  values: UserSchema
  errors: UserFormErrors
  onChange: (patch: Partial<UserSchema>) => void
  isNewUser: boolean
  traders: Trader[] | undefined
  onSubmit: () => void | Promise<void>
  cancelPath: string
  title: string
  loadingTraders: boolean
  loadingUser?: boolean
  submitting: boolean
  currentUserIsSuperAdmin?: boolean
  rootAction?: React.ReactNode
}

const UserFormCommon: React.FC<UserFormCommonProps> = ({
  values,
  errors,
  onChange,
  isNewUser,
  traders,
  onSubmit,
  cancelPath,
  title,
  loadingTraders,
  loadingUser = false,
  submitting,
  currentUserIsSuperAdmin = false,
  rootAction
}) => {
  if (loadingUser || loadingTraders) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading...</div>
  }

  const traderId = values.roles?.[0]?.traderId
  const selectedTrader = isNumber(traderId) ? traders?.find((t) => t.id === traderId) : undefined
  const showKlearRoles = selectedTrader?.isKlear === true || isKlearRoleWire({ roles: values.roles ?? [] })

  const hasRole = (role: string) => values.roles?.some((r) => r.role === role) || false

  const handleRoleToggle = (role: UserRoleName) => {
    const currentTraderId = values.roles?.[0]?.traderId
    onChange({ roles: [{ role, traderId: currentTraderId }] })
  }

  const handleTraderChange = (value: number | '') => {
    const nextTraderId = value === '' ? undefined : value
    const nextTrader = isNumber(nextTraderId) ? traders?.find((t) => t.id === nextTraderId) : undefined
    const currentRole = values.roles?.[0]?.role ?? ''
    const roleStillApplies =
      !currentRole || (nextTrader?.isKlear === true ? isKlearRoleName(currentRole) : !isKlearRoleName(currentRole))
    onChange({ roles: [{ role: roleStillApplies ? currentRole : '', traderId: nextTraderId }] })
  }

  return (
    <div className="p-6">
      <div className="bg-background rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
            <Link to={cancelPath} className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/70">
              Cancel
            </Link>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void onSubmit()
          }}
        >
          <div className="space-y-6 p-6">
            {errors.root && (
              <div className="mb-4 p-3 flex items-center justify-between gap-4 bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 rounded-md">
                {errors.root}
                {rootAction}
              </div>
            )}

            <section>
              <h2 className="text-lg font-medium text-foreground mb-4">User Information</h2>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Work Email</Label>
                  <Input
                    id="email"
                    value={values.email}
                    disabled={!isNewUser}
                    onChange={(e) => onChange({ email: e.target.value })}
                    className={cn(!isNewUser ? 'bg-muted' : '', errors.email ? 'border-destructive' : '')}
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={values.firstName}
                    onChange={(e) => onChange({ firstName: e.target.value })}
                    className={errors.firstName ? 'border-destructive' : ''}
                  />
                  {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="middleName">Middle Name</Label>
                  <Input
                    id="middleName"
                    value={values.middleName ?? ''}
                    onChange={(e) => onChange({ middleName: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={values.lastName}
                    onChange={(e) => onChange({ lastName: e.target.value })}
                    className={errors.lastName ? 'border-destructive' : ''}
                  />
                  {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone</Label>
                  <Input
                    id="phoneNumber"
                    value={values.phoneNumber ?? ''}
                    onChange={(e) => onChange({ phoneNumber: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jobTitle">Job Title</Label>
                  <Input
                    id="jobTitle"
                    value={values.jobTitle}
                    onChange={(e) => onChange({ jobTitle: e.target.value })}
                    className={errors.jobTitle ? 'border-destructive' : ''}
                  />
                  {errors.jobTitle && <p className="text-sm text-destructive">{errors.jobTitle}</p>}
                </div>
              </div>
            </section>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="trader">Select Trader</Label>
                <Combobox<number>
                  value={isNumber(traderId) ? traderId : ''}
                  options={(traders ?? [])
                    .filter((trader) => currentUserIsSuperAdmin || !trader.isKlear)
                    .map((trader) => ({
                      value: toNumber(trader.id),
                      label: trader.name
                    }))}
                  onChange={handleTraderChange}
                  placeholder="Select a trader..."
                  valueType="number"
                  className="w-full"
                />
              </div>
            </div>

            <section>
              <h2 className="text-lg font-medium text-foreground mb-4">Role</h2>
              {!isNumber(traderId) && (
                <p className="mb-3 text-sm text-muted-foreground">
                  Select a trader above before saving. You can choose a role first.
                </p>
              )}
              <div className={errors.roles ? 'border-l-4 border-red-500 pl-3' : ''}>
                <div className="space-y-2">
                  {showKlearRoles ? (
                    <>
                      <div className="flex items-center">
                        <input
                          id="role-csm"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_KLEAR_CSM)}
                          onChange={() => handleRoleToggle(ROLE_KLEAR_CSM)}
                        />
                        <label htmlFor="role-csm" className="ml-2 block text-sm text-foreground">
                          Customer Success Manager (CSM)
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="role-readonly"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_KLEAR_READONLY)}
                          onChange={() => handleRoleToggle(ROLE_KLEAR_READONLY)}
                        />
                        <label htmlFor="role-readonly" className="ml-2 block text-sm text-foreground">
                          Read Only
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="role-funder"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_KLEAR_FUNDER)}
                          onChange={() => handleRoleToggle(ROLE_KLEAR_FUNDER)}
                        />
                        <label htmlFor="role-funder" className="ml-2 block text-sm text-foreground">
                          Funder
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="role-super-admin"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_KLEAR_SUPER_ADMIN)}
                          onChange={() => handleRoleToggle(ROLE_KLEAR_SUPER_ADMIN)}
                        />
                        <label htmlFor="role-super-admin" className="ml-2 block text-sm text-foreground">
                          Super Admin
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center">
                        <input
                          id="role-user"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_TRADER_USER)}
                          onChange={() => handleRoleToggle(ROLE_TRADER_USER)}
                        />
                        <label htmlFor="role-user" className="ml-2 block text-sm text-foreground">
                          User
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="role-admin"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_TRADER_ADMIN)}
                          onChange={() => handleRoleToggle(ROLE_TRADER_ADMIN)}
                        />
                        <label htmlFor="role-admin" className="ml-2 block text-sm text-foreground">
                          {ROLE_LABELS[ROLE_TRADER_ADMIN]}
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="role-financial-admin"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_TRADER_FINANCIAL_ADMIN)}
                          onChange={() => handleRoleToggle(ROLE_TRADER_FINANCIAL_ADMIN)}
                        />
                        <label htmlFor="role-financial-admin" className="ml-2 block text-sm text-foreground">
                          Financial Admin
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="role-org-owner"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_TRADER_ORGANIZATION_OWNER)}
                          onChange={() => handleRoleToggle(ROLE_TRADER_ORGANIZATION_OWNER)}
                        />
                        <label htmlFor="role-org-owner" className="ml-2 block text-sm text-foreground">
                          Organization Owner
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="role-readonly"
                          type="radio"
                          name="role"
                          className="h-4 w-6 text-blue-600 focus:ring-blue-500 border-border rounded-full"
                          checked={hasRole(ROLE_TRADER_READONLY)}
                          onChange={() => handleRoleToggle(ROLE_TRADER_READONLY)}
                        />
                        <label htmlFor="role-readonly" className="ml-2 block text-sm text-foreground">
                          Read Only
                        </label>
                      </div>
                    </>
                  )}
                  {errors.roles && <p className="mt-1 text-sm text-red-500">{errors.roles}</p>}
                </div>
              </div>
            </section>

            <div className="flex justify-end mt-6 gap-4">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded"
                aria-label="Save User Details"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <Link
                to={cancelPath}
                className="text-foreground bg-muted hover:bg-muted/70 py-2 px-4 rounded"
                aria-label="Cancel"
              >
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UserFormCommon
